import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import React,{ useState } from 'react'
import { motion } from 'framer-motion'
import { Lock,Loader2,CheckCircle2,AlertCircle } from 'lucide-react'
import { createFileRoute,useNavigate,redirect } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card,CardContent,CardHeader,CardTitle } from '@/components/ui/card'
import { api } from '@/lib/api-client'
import { z } from 'zod'

const resetSearchSchema = z.object({
  token: z.string().optional(),
})

export const Route = createFileRoute('/reset-password')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  validateSearch: resetSearchSchema,
  component: ResetPasswordPage,
  beforeLoad: ({ search }) => {
    if (!search.token) {
      throw redirect({ to: '/' })
    }
  },
})

function ResetPasswordPage() {
  const navigate = useNavigate()
  const { token } = Route.useSearch()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      await api.post(`/auth/reset-password`,
        { token, newPassword },
        { headers: { 'x-skip-auth-redirect': '1' } }
      )
      setDone(true)
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(
        e.response?.data?.message || 'Invalid or expired reset link. Please request a new one.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <Card className="shadow-xl">
          <CardHeader className="space-y-4 text-center">
            <motion.div layout className="mx-auto w-16 h-16 flex items-center justify-center overflow-hidden">
              <>
                <img
                  src="https://cdn.rootpoint.io/platform/logo-color.svg"
                  alt="RootPoint Logo"
                  className="w-full h-full object-contain dark:hidden"
                />
                <img
                  src="https://cdn.rootpoint.io/platform/logo-color.svg"
                  alt="RootPoint Logo"
                  className="w-full h-full object-contain hidden dark:block"
                />
              </>
            </motion.div>
            <div>
              <CardTitle className="text-3xl font-bold text-balance">
                {done ? 'Password Reset' : 'Set New Password'}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                {done ? 'Your password has been updated.' : 'Choose a strong new password.'}
              </p>
            </div>
          </CardHeader>

          <CardContent>
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center gap-4 py-4 text-center"
              >
                <CheckCircle2 className="h-12 w-12 text-emerald-500" />
                <p className="text-sm text-muted-foreground">
                  You can now sign in with your new password.
                </p>
                <Button className="w-full h-11 mt-2" onClick={() => navigate({ to: '/' })}>
                  Go to Sign In
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <Input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value)
                          setError(null)
                        }}
                        className="pl-10 h-11 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-primary"
                        placeholder="••••••••"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value)
                          setError(null)
                        }}
                        className="pl-10 h-11 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-primary"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 transition-all"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-sm text-destructive font-medium"
                  >
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {error}
                  </motion.div>
                )}
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
      <div className="mt-8 text-center text-xs text-muted-foreground">
        &copy; {new Date().getFullYear()} RootPoint. All rights reserved.
      </div>
    </div>
  )
}
